import React, { useEffect, useRef, useState } from 'react';
import { FaSync, FaTimes, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import Hls from 'hls.js';

interface StagePlayerProps {
  playbackUrl: string;
  isFullscreen?: boolean;
  onClose?: () => void;
  onMinimize?: () => void;
  className?: string;
  isMuted?: boolean;
  onToggleMute?: () => void;
}

const StagePlayer: React.FC<StagePlayerProps> = ({ 
  playbackUrl, 
  isFullscreen = false, 
  onClose,
  onMinimize,
  className = '',
  isMuted: externalIsMuted,
  onToggleMute: externalOnToggleMute
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasStream, setHasStream] = useState(false);
  const [internalIsMuted, setInternalIsMuted] = useState(true); // Muted by default
  
  // Use external muted state if provided, otherwise use internal
  const isMuted = externalIsMuted !== undefined ? externalIsMuted : internalIsMuted;

  // Sync video element muted property with state
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl) return;

    setIsLoading(true);
    setError(null);
    setHasStream(false);

    let hls: Hls | null = null;

    // Check if HLS.js is supported
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = playbackUrl;
      
      const handleLoadedMetadata = () => {
        setIsLoading(false);
        setHasStream(true);
        video.play().catch(err => {
          console.error('Error playing video:', err);
          setError('Failed to play stream');
        });
      };

      const handleError = () => {
        setIsLoading(false);
        setError('Stream is offline or unavailable');
        setHasStream(false);
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('error', handleError);

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('error', handleError);
      };
    } else {
      // Use HLS.js for browsers that don't support native HLS
      if (Hls.isSupported()) {
        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 90
        });
        
        hls.loadSource(playbackUrl);
        hls.attachMedia(video);
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false);
          setHasStream(true);
          video.play().catch(err => {
            console.error('Error playing video:', err);
            setError('Failed to play stream');
          });
        });
        
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            setIsLoading(false);
            setError('Stream is offline or unavailable');
            setHasStream(false);
            console.error('HLS error:', data);
          }
        });
        
        return () => {
          if (hls) {
            hls.destroy();
          }
        };
      } else {
        setIsLoading(false);
        setError('HLS playback not supported in this browser');
      }
    }
  }, [playbackUrl]);

  const handleRefresh = () => {
    const video = videoRef.current;
    if (!video) return;

    setIsLoading(true);
    setError(null);
    
    // Force reload by updating the src
    const currentTime = video.currentTime;
    video.load();
    video.currentTime = currentTime;
    
    video.play().catch(err => {
      console.error('Error playing video:', err);
      setError('Failed to play stream');
    });
    
    setTimeout(() => setIsLoading(false), 1000);
  };

  const handleToggleMute = () => {
    if (externalOnToggleMute) {
      externalOnToggleMute();
    } else {
      setInternalIsMuted(!internalIsMuted);
    }
  };

  if (!playbackUrl) {
    return (
      <div className={`flex items-center justify-center bg-black ${className}`}>
        <div className="text-center text-white">
          <p className="text-xl">No stage stream configured</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative bg-black ${className}`}>
      {/* Video Element */}
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        autoPlay
        muted={isMuted}
        playsInline
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70">
          <div className="text-center text-white">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-lg">Loading stream...</p>
          </div>
        </div>
      )}

      {/* Error/Offline Message */}
      {error && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-center text-white p-8">
            <div className="text-6xl mb-4"></div>
            <p className="text-2xl mb-2">Stream Offline</p>
            <p className="text-gray-400 mb-4">{error}</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRefresh();
              }}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
            >
              <FaSync />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Refresh Control and Audio for Fullscreen */}
      {hasStream && !error && isFullscreen && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRefresh();
              }}
              className="p-3 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-all"
              title="Refresh stream"
            >
              <FaSync size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleMute();
              }}
              className="p-3 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-all"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <FaVolumeMute size={16} /> : <FaVolumeUp size={16} />}
            </button>
          </div>
        </div>
      )}

      {/* Close/Exit Buttons */}
      {isFullscreen && onClose && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 p-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2 z-10"
        >
          <FaTimes size={16} />
          Exit Stage View
        </button>
      )}
      
      {/* Minimize button for mini player */}
      {!isFullscreen && onMinimize && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMinimize();
          }}
          className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all z-10"
          title="Minimize and stop stream"
        >
          <FaTimes size={14} />
        </button>
      )}
      
      {/* Controls for mini player - bottom bar */}
      {!isFullscreen && hasStream && !error && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRefresh();
              }}
              className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-all"
              title="Refresh stream"
            >
              <FaSync size={12} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleMute();
              }}
              className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-all"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <FaVolumeMute size={12} /> : <FaVolumeUp size={12} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StagePlayer;

